import { TextInput } from "@mantine/core";
import { useDebouncedValue, useDocumentTitle } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Column } from "react-table";
import styled, { css } from "styled-components";
import { Heading } from "../../components/styled";
import { TransactionsTable } from "../../components/tables/PaginatedTable";
import { useRequestService } from "../../hooks/useRequests";
import { device } from "../../lib/constants";
import { getAccessRequestsTableColumnConfig } from "./../requests-table-config";

const Wrapper = styled.div`
  padding: 40px 64px 0 64px;
  @media ${device.tablet} {
    padding: 40px 16px 0 16px;
  }
`;
const { useGetAccessRequests } = useRequestService()

export const Requests = (): JSX.Element => {
	const { t } = useTranslation();
	useDocumentTitle(`DAP: ${t("navigation.requests")}`);

	const [queryPageIndex, setQueryPageIndex] = useState(0);
	const [queryPageSize, setQueryPageSize] = useState(10);
	const [searchString, setSearchString] = useState("");

	const [debouncedSearchString] = useDebouncedValue(searchString, 1500, { leading: true });

	const {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		data = [] as any,
		isLoading: isLoadingRequests,
		isFetching,
		isPreviousData,
	} = useGetAccessRequests(
		queryPageIndex,
		queryPageSize,
    ...debouncedSearchString ? debouncedSearchString : ""
	);

	const requests = data?.content || [];

	const columnPropGetter = (col: Column<Request>) => {
		const { id } = col;
		let textAlign: "start" | "end";
		switch (id) {
			case "Actions":
				textAlign = "end";
				break;
			default:
				textAlign = "start";
				break;
		}
		return {
			style: {
				textAlign,
			},
		};
	};

	return (
		<Wrapper>
			<Heading>Manage Requests</Heading>
			<RequestsSubHeading>Take action on wallet requests</RequestsSubHeading>
			<RequestsArea>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<RequestsSubHeading
						style={{ textTransform: "capitalize", color: "#687D94" }}
					>
						{data?.totalElements} Access Requests
					</RequestsSubHeading>
					<TextInput
            wrapperProps={{
              root: {
                backgroundColor: "var(--accentBg)",
                border: "none",
              }
            }}
						value={searchString}
						placeholder="Search Requests"
						onChange={(event) => setSearchString(event.currentTarget.value)}
					/>
				</div>
				<TransactionsTable<Request>
					columnConfig={getAccessRequestsTableColumnConfig(t)}
					loading={isLoadingRequests || isFetching}
					totalPages={data?.totalPages}
					rowData={requests}
					hideTotal
					title={t("access.requests")}
					getColumnProps={columnPropGetter}
					hideFilters
					queryPageIndex={queryPageIndex}
					queryPageSize={queryPageSize}
					setQueryPageIndex={setQueryPageIndex}
					setQueryPageSize={setQueryPageSize}
					totalItems={data?.totalElements}
					isPreviousData={isPreviousData}
				/>
			</RequestsArea>
		</Wrapper>
	);
};

const RequestsArea = styled.div`
  margin-top: 50px;
`;

const RequestsSubHeading = styled.h2`
  font-size: 14px;
  font-family: 'ProximaNova', sans-serif;
  line-height: 18px;
  color: var(--textColor);
`;
const _baseBoxStyles = css`
  border: 1px solid #3f5064;
  border-radius: 8px;
  width: 100%;
  padding: 68px 24px 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background-repeat: no-repeat;
  background-position: 24px 28px;
  background-color: ${({ theme }) => theme.colors.primary.darkText};
  height: 270px;
  background-size: 30px;
  transition: 0.3s ease-in-out;
  &:not(.disabled):hover {
    background-color: ${({ theme }) => theme.colors.primary.green};
    div.button {
      background-color: ${({ theme }) => theme.colors.primary.white};
    }
    p,
    h2 {
      color: ${({ theme }) => theme.colors.primary.white};
    }
  }
  &.disabled {
    background-color: #273443;
  }
  &.disabled p,
  &.disabled .button {
    color: ${({ theme }) => theme.colors.secondary.grey};
  }
`;
